import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Dialog } from "@ui/Dialog"
import { EmptyState } from "@ui/EmptyState"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Pill } from "@ui/Pill"
import { Select } from "@ui/Select"
import type { Project } from "@lib/inertia-types"

const MASK = "********"

const KIND_LABELS: Record<string, string> = {
  bearer_token: "Bearer token",
  basic: "Basic auth",
  api_key_header: "API key header",
  oauth2_client_credentials: "OAuth2 client credentials",
}

const KIND_FIELDS: Record<string, { name: string; label: string; secret: boolean; placeholder?: string }[]> = {
  bearer_token: [{ name: "token", label: "Token", secret: true }],
  basic: [
    { name: "username", label: "Username", secret: false },
    { name: "password", label: "Password", secret: true },
  ],
  api_key_header: [
    { name: "header_name", label: "Header name", secret: false, placeholder: "X-Api-Key" },
    { name: "api_key", label: "API key", secret: true },
  ],
  oauth2_client_credentials: [
    { name: "token_url", label: "Token URL", secret: false, placeholder: "https://idbroker.webex.com/idb/oauth2/v1/access_token" },
    { name: "client_id", label: "Client ID", secret: false },
    { name: "client_secret", label: "Client secret", secret: true },
    { name: "scope", label: "Scope (optional)", secret: false },
    { name: "audience", label: "Audience (optional)", secret: false },
    { name: "auth_style", label: "Client auth style: basic or body (optional)", secret: false, placeholder: "basic" },
  ],
}

interface CredentialRow {
  id: number
  name: string
  kind: string
  field_names: string[]
  filled_fields: string[]
}

export default function CredentialsIndex({
  project,
  credentials,
}: {
  project: Project
  credentials: CredentialRow[]
}) {
  const [editing, setEditing] = React.useState<CredentialRow | "new" | null>(null)

  function deleteCredential(credential: CredentialRow) {
    if (!confirm(`Delete credential "${credential.name}"? Requests using it fall back to no auth.`)) return
    router.delete(`/projects/${project.slug}/credentials/${credential.id}`, {
      preserveScroll: true,
      only: ["credentials"],
    })
  }

  return (
    <>
      <Head title={`Credentials · ${project.name} · Fetch`} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              Credentials
            </h1>
            <p className="mt-1 text-small text-cloverhound-gray-600">
              Secrets are encrypted at rest and write-only — they are injected
              server-side when a request runs and never sent back to the browser.
            </p>
          </div>
          <Button onClick={() => setEditing("new")}>
            <Plus className="size-4" aria-hidden="true" />
            New credential
          </Button>
        </div>

        {credentials.length === 0 ? (
          <EmptyState
            icon={<KeyRound className="size-6" aria-hidden="true" />}
            title="No credentials yet"
            description="Store bearer tokens, basic auth, API keys, or OAuth2 client credentials once, then reference them from folders and requests."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {credentials.map((credential) => (
              <Card key={credential.id} padding="md">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
                        {credential.name}
                      </h2>
                      <Pill>{KIND_LABELS[credential.kind] ?? credential.kind}</Pill>
                    </div>
                    <p className="mt-0.5 font-mono text-micro text-cloverhound-gray-600">
                      {credential.field_names
                        .map((f) => `${f}${credential.filled_fields.includes(f) ? " ✓" : ""}`)
                        .join(" · ")}
                    </p>
                  </div>
                  <Button variant="ghost" size="iconSm" onClick={() => setEditing(credential)} aria-label="Edit">
                    <Pencil className="size-4" aria-hidden="true" />
                  </Button>
                  <Button variant="ghost" size="iconSm" onClick={() => deleteCredential(credential)} aria-label="Delete">
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <CredentialDialog
          project={project}
          credential={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}

function CredentialDialog({
  project,
  credential,
  onClose,
}: {
  project: Project
  credential: CredentialRow | null
  onClose: () => void
}) {
  const [name, setName] = React.useState(credential?.name ?? "")
  const [kind, setKind] = React.useState(credential?.kind ?? "bearer_token")
  const [data, setData] = React.useState<Record<string, string>>(() => {
    if (!credential) return {}
    return Object.fromEntries(credential.filled_fields.map((f) => [f, MASK]))
  })
  const [submitting, setSubmitting] = React.useState(false)

  const fields = KIND_FIELDS[kind] ?? []

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    const opts = { preserveScroll: true, only: ["credentials"], onFinish: () => (setSubmitting(false), onClose()) }
    if (credential) {
      router.patch(`/projects/${project.slug}/credentials/${credential.id}`, { name, data }, opts)
    } else {
      router.post(`/projects/${project.slug}/credentials`, { name, kind, data }, opts)
    }
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content size="sm">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>{credential ? `Edit ${credential.name}` : "New credential"}</Dialog.Title>
            {credential && (
              <Dialog.Description>
                Masked values (••••) keep their stored secret; type to replace.
              </Dialog.Description>
            )}
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            <Field label="Name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Webex CC prod"
                autoFocus={!credential}
              />
            </Field>
            {!credential && (
              <Field label="Type">
                <Select.Root value={kind} onValueChange={(v) => v && (setKind(v), setData({}))}>
                  <Select.Trigger aria-label="Credential type">
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content>
                    {Object.entries(KIND_LABELS).map(([value, label]) => (
                      <Select.Item key={value} value={value}>
                        {label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Field>
            )}
            {fields.map((field) => (
              <Field key={field.name} label={field.label}>
                <Input
                  type={field.secret ? "password" : "text"}
                  value={data[field.name] ?? ""}
                  onChange={(e) => setData((d) => ({ ...d, [field.name]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="font-mono"
                  autoComplete="off"
                />
              </Field>
            ))}
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={!name.trim()}>
              {credential ? "Save" : "Create"}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
